import React, { useState, useEffect, useMemo } from "react";
import { Modal, Table, Spin, Button, Tabs, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { translations } from "../../utils/Translation/staticTranslation";
import { Campaigns as CampaignsAPI } from "../../Apis/campaignsApi";
import FailedEmailsSection from "./FailedEmailsSection";
import "./Campaigns.css";

const formatStatDate = (iso) => {
  if (!iso) return "—";
  const d = dayjs(iso);
  return d.isValid() ? d.format("YYYY-MM-DD HH:mm") : "—";
};

const CampaignStatisticsModal = ({ visible, onCancel, campaignId, readOnly = false, canDelete = true }) => {
  const lang = localStorage.getItem("language") || "ar";
  const t = translations[lang]?.campaignsPage;
  const common_btn = translations[lang]?.common_btn;
  const statusLogsT = t?.status_logs || {};

  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);

  useEffect(() => {
    if (visible && campaignId) {
      fetchStatistics();
    }
  }, [visible, campaignId]);

  const fetchStatistics = async ({ skipCache = false, silent = false } = {}) => {
    if (!campaignId) return;

    if (!silent) setLoading(true);
    try {
      const response = await CampaignsAPI.getCampaignSummary(campaignId, { skipCache });
      if (response?.data?.result) {
        setStatistics(response.data.result);
      } else if (response?.result) {
        setStatistics(response.result);
      } else {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error("Error fetching campaign statistics:", error);
      message.error(
        lang === "ar"
          ? "فشل في جلب إحصائيات الحملة"
          : "Failed to fetch campaign statistics"
      );
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // After failed emails are removed, refresh the summary counts without
  // unmounting the failed-emails table (silent) and bypassing the GET cache.
  const handleFailedEmailsChanged = () =>
    fetchStatistics({ skipCache: true, silent: true });

  const openedUsersColumns = useMemo(
    () => [
      {
        title:
          statusLogsT.email || (lang === "ar" ? "البريد الإلكتروني" : "Email"),
        dataIndex: "email",
        key: "email",
        render: (text) => text || "—",
      },
      {
        title:
          statusLogsT.open_count || (lang === "ar" ? "مرات الفتح" : "Open count"),
        dataIndex: "open_count",
        key: "open_count",
        width: 110,
      },
      {
        title:
          statusLogsT.first_opened ||
          (lang === "ar" ? "أول فتح" : "First opened"),
        dataIndex: "first_opened_at",
        key: "first_opened_at",
        render: (v) => formatStatDate(v),
      },
    ],
    [lang]
  );

  const openStats = statistics?.opened_emails;
  const openedUsers = useMemo(
    () =>
      [...(openStats?.opened_users || [])].sort(
        (a, b) => (Number(b.open_count) || 0) - (Number(a.open_count) || 0)
      ),
    [openStats]
  );

  const downloadOpenedUsers = () => {
    const escapeCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const header = ["Email", "Open count", "First opened"];
    const rows = openedUsers.map((u) => [
      u.email,
      u.open_count ?? 0,
      u.first_opened_at ? formatStatDate(u.first_opened_at) : "",
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\n");

    // BOM so Excel reads UTF-8 (Arabic) correctly
    const blob = new Blob(["﻿" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `campaign-${campaignId}-opened-${dayjs().format(
      "YYYY-MM-DD"
    )}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Modal
      title={
        statusLogsT.title ||
        (lang === "ar" ? "سجلات إحصائيات الحملة" : "Campaign Status Logs")
      }
      open={visible}
      onCancel={onCancel}
      width={900}
      zIndex={3000}
      footer={[
        <button key="close" className="cancel-btn" onClick={onCancel}>
          {common_btn?.close || (lang === "ar" ? "إغلاق" : "Close")}
        </button>,
      ]}
      className="campaign-statistics-modal logs-view-modal"
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
        </div>
      ) : statistics ? (
        <div className="logs-modal-content">
          {/* Summary Section */}
          <div className="logs-summary-section">
            <div className="logs-summary-container campaign-stats-container">
              <h5 className="logs-summary-title">
                {statusLogsT.upload_summary ||
                  (lang === "ar" ? "ملخص التحميل" : "Upload Summary")}
              </h5>
              <div className="logs-summary-stats campaign-stats-grid">
                <div className="logs-summary-stat">
                  <strong>
                    {statusLogsT.total_emails ||
                      (lang === "ar" ? "إجمالي الرسائل:" : "Total Emails:")}
                  </strong>{" "}
                  {statistics.total_emails || 0}
                </div>
                <div className="logs-summary-stat">
                  <strong>
                    {statusLogsT.successful_emails ||
                      (lang === "ar" ? "الرسائل الناجحة:" : "Successful Emails:")}
                  </strong>{" "}
                  {statistics.successful_emails || 0}
                </div>
                <div className="logs-summary-stat">
                  <strong>
                    {statusLogsT.failed_emails ||
                      (lang === "ar" ? "الرسائل الفاشلة:" : "Failed Emails:")}
                  </strong>{" "}
                  {statistics.failed_emails || 0}
                </div>
                {openStats && typeof openStats === "object" && (
                  <>
                    <div className="logs-summary-stat">
                      <strong>
                        {statusLogsT.opened_tracked ||
                          (lang === "ar"
                            ? "رسائل مفتوحة (متتبع):"
                            : "Opened (tracked):")}
                      </strong>{" "}
                      {openStats.opened_emails ?? 0}
                    </div>
                    <div className="logs-summary-stat">
                      <strong>
                        {statusLogsT.unopened ||
                          (lang === "ar" ? "غير مفتوحة:" : "Unopened:")}
                      </strong>{" "}
                      {openStats.unopened_emails ?? 0}
                    </div>
                  </>
                )}
                {statistics.send_status && (
                  <div className="logs-summary-stat">
                    <strong>
                      {statusLogsT.send_status ||
                        (lang === "ar" ? "حالة الإرسال:" : "Send Status:")}
                    </strong>{" "}
                    {statistics.send_status}
                  </div>
                )}
                {statistics.processed_batches !== undefined &&
                  statistics.total_batches !== undefined && (
                    <div className="logs-summary-stat">
                      <strong>
                        {statusLogsT.processed_batches ||
                          (lang === "ar"
                            ? "الدفعات المعالجة:"
                            : "Processed Batches:")}
                      </strong>{" "}
                      {statistics.processed_batches} /{" "}
                      {statistics.total_batches}
                    </div>
                  )}
              </div>
            </div>
          </div>

          <Tabs
            defaultActiveKey="opened"
            className="campaign-stats-tabs"
            items={[
              {
                key: "opened",
                label: `${
                  statusLogsT.recipients_who_opened ||
                  (lang === "ar"
                    ? "المستلمون الذين فتحوا الرسالة"
                    : "Recipients who opened")
                } (${openedUsers.length})`,
                children:
                  openedUsers.length > 0 ? (
                    <>
                      <div className="campaign-stats-tab-toolbar">
                        <Button
                          icon={<DownloadOutlined />}
                          onClick={downloadOpenedUsers}
                        >
                          {statusLogsT.download_opened ||
                            (lang === "ar" ? "تنزيل القائمة" : "Download list")}
                        </Button>
                      </div>
                      <Table
                        dataSource={openedUsers}
                        columns={openedUsersColumns}
                        rowKey={(record, index) =>
                          record.email ? `open-${record.email}` : `open-${index}`
                        }
                        pagination={{
                          pageSize: 10,
                          showSizeChanger: false,
                          showTotal: (total) =>
                            `${total} ${
                              statusLogsT.items ||
                              (lang === "ar" ? "عنصر" : "items")
                            }`,
                        }}
                        scroll={{ x: 640 }}
                        className="logs-error-table campaign-opened-users-table"
                      />
                    </>
                  ) : (
                    <div className="logs-no-errors">
                      <p>
                        {statusLogsT.no_opened ||
                          (lang === "ar"
                            ? "لم يفتح أي مستلم الرسالة بعد"
                            : "No recipients have opened yet")}
                      </p>
                    </div>
                  ),
              },
              {
                key: "failed",
                label: `${
                  translations[lang]?.campaignsPage?.failed_emails?.title ||
                  (lang === "ar" ? "الرسائل الفاشلة" : "Failed Emails")
                } (${statistics.failed_emails || 0})`,
                children: (
                  <FailedEmailsSection
                    campaignId={campaignId}
                    readOnly={readOnly}
                    canDelete={canDelete}
                    onChanged={handleFailedEmailsChanged}
                  />
                ),
              },
            ]}
          />
        </div>
      ) : (
        <div className="logs-no-errors">
          <p>
            {statusLogsT.no_data || (lang === "ar" ? "لا توجد بيانات متاحة" : "No data available")}
          </p>
        </div>
      )}
    </Modal>
  );
};

export default CampaignStatisticsModal;
